import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleBestPlacesOrganicGrocery() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "best-places-for-organic-grocery-in-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "why-organic": "Why Organic Grocery Matters",
    "top-locations": "Top Organic Grocery Locations",
    certifications: "Organic Certifications",
    "local-regulations": "Local Regulations & Best Practices",
    "practical-tips": "Practical Tips for Organic Shopping",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://vancouver.ca/home-property-development/green-bin.aspx",
      label: "City of Vancouver – Green Bin Program",
    },
    {
      url: "https://www.destinationvancouver.com",
      label: "Tourism Vancouver",
    },
    {
      url: "https://bcfarmersmarket.org",
      label: "BC Association of Farmers’ Markets",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  const locations = [
    {
      id: "whole-foods-cambie",
      lat: 49.2633,
      lng: -123.1152,
      popupText: "Whole Foods Market (Cambie)",
    },
    {
      id: "choices-markets-cambie",
      lat: 49.2538,
      lng: -123.1152,
      popupText: "Choices Markets (Cambie)",
    },
    {
      id: "nada",
      lat: 49.2637,
      lng: -123.0950,
      popupText: "Nada Zero-Waste Grocery",
    },
    {
      id: "the-drive-organics",
      lat: 49.2697,
      lng: -123.0692,
      popupText: "The Drive Organics",
    },
    {
      id: "granville-island",
      lat: 49.2714,
      lng: -123.1334,
      popupText: "Granville Island Public Market",
    },
    {
      id: "urban-fare-yaletown",
      lat: 49.2733,
      lng: -123.1213,
      popupText: "Urban Fare (Yaletown)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Best Places for Organic Grocery in Vancouver: A Complete Guide</title>
        <meta
          name="description"
          content="Explore top organic grocery locations in Vancouver, including store details, practical tips, and local regulations."
        />
        <meta
          name="keywords"
          content="organic grocery, Vancouver, sustainability, farmers market, nutrition"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/best-places-for-organic-grocery-in-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Best Places for Organic Grocery in Vancouver: A Complete Guide"
        />
        <meta
          property="og:description"
          content="A practical guide to Vancouver’s best organic grocery spots, with tips on local regulations and sustainable shopping."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/best-places-for-organic-grocery-in-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/best-places-for-organic-grocery-in-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Best Places for Organic Grocery in Vancouver: A Complete Guide"
        />
        <meta
          name="twitter:description"
          content="Check out Vancouver’s top organic grocery stores, helpful shopping tips, and local rules for sustainable living."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/best-places-for-organic-grocery-in-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Fresh organic produce in a Vancouver grocery store"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver is renowned for its health-conscious community, vibrant
              food culture, and commitment to sustainability. Finding
              high-quality organic groceries can be an exciting (and delicious)
              adventure. This guide walks you through some of the best organic
              grocery spots in the city—complete with addresses, coordinates,
              operating hours, and tips to help you shop smart and sustainably.
            </p>

            {/* SECTION: Why Organic Grocery Matters */}
            <h2 id="why-organic">Why Organic Grocery Matters</h2>
            <p>
              <strong>Health Benefits:</strong> Organic foods often contain fewer
              synthetic pesticides, offering a cleaner eating experience.
              <br />
              <strong>Environmental Impact:</strong> Organic farming practices
              can reduce soil erosion and support biodiversity.
              <br />
              <strong>Local Economy:</strong> Many organic stores partner with
              local farmers, bolstering community growth and sustainability.
            </p>

            {/* SECTION: Top Organic Grocery Locations */}
            <h2 id="top-locations">Top Organic Grocery Locations</h2>
            <p>
              Below are some of Vancouver’s most popular spots for organic
              groceries, each offering unique benefits and highlights.
            </p>

            {/* Whole Foods Market (Cambie) */}
            <h3>Whole Foods Market (Cambie)</h3>
            <p
              ref={(el) => setSpotRef("whole-foods-cambie", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates: </strong>
              <Marker
                coordY={49.2633}
                coordX={-123.1152}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 510 W 8th Ave, Vancouver, BC V5Z 1C5
            </p>
            <ul>
              <li>Operating Hours: 8:00 AM – 10:00 PM (varies on holidays)</li>
              <li>
                Large selection of certified organic produce and specialty items
              </li>
              <li>
                In-store café, hot bar, and a wide range of dietary-specific
                products
              </li>
            </ul>

            {/* Choices Markets (Cambie) */}
            <h3>Choices Markets (Cambie)</h3>
            <p
              ref={(el) => setSpotRef("choices-markets-cambie", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates: </strong>
              <Marker
                coordY={49.2538}
                coordX={-123.1152}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 3493 Cambie St, Vancouver, BC V5Z 2W7
            </p>
            <ul>
              <li>Operating Hours: 8:00 AM – 9:00 PM daily</li>
              <li>
                Proudly local chain with a large organic produce section and
                natural health products
              </li>
              <li>Bakery offering organic breads and pastries</li>
            </ul>

            {/* Nada */}
            <h3>Nada</h3>
            <p
              ref={(el) => setSpotRef("nada", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates: </strong>
              <Marker
                coordY={49.2637}
                coordX={-123.0950}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 675 E Broadway, Vancouver, BC V5T 1X7
            </p>
            <ul>
              <li>Operating Hours: 10:00 AM – 7:00 PM (Tues–Sun), closed Mon</li>
              <li>
                Zero-waste grocery store with a focus on local, organic
                produce—bring your own containers!
              </li>
              <li>Excellent selection of bulk items and eco-friendly products</li>
            </ul>

            {/* The Drive Organics */}
            <h3>The Drive Organics</h3>
            <p
              ref={(el) => setSpotRef("the-drive-organics", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates: </strong>
              <Marker
                coordY={49.2697}
                coordX={-123.0692}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 1738 Commercial Dr, Vancouver, BC V5N 4A3
            </p>
            <ul>
              <li>Operating Hours: 9:00 AM – 9:00 PM daily</li>
              <li>
                Located in the heart of Commercial Drive’s eclectic neighborhood
              </li>
              <li>Frequent promotions on seasonal organic produce</li>
            </ul>

            {/* Granville Island Public Market */}
            <h3>Granville Island Public Market</h3>
            <p
              ref={(el) => setSpotRef("granville-island", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates: </strong>
              <Marker
                coordY={49.2714}
                coordX={-123.1334}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 1689 Johnston St, Vancouver, BC V6H 3R9
            </p>
            <ul>
              <li>Operating Hours: 9:00 AM – 6:00 PM daily</li>
              <li>
                Multiple vendors offering organic produce, meats, and specialty
                foods
              </li>
              <li>Enjoy a lively atmosphere with artisan shops and waterfront views</li>
            </ul>

            {/* Urban Fare (Yaletown) */}
            <h3>Urban Fare (Yaletown)</h3>
            <p
              ref={(el) => setSpotRef("urban-fare-yaletown", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates: </strong>
              <Marker
                coordY={49.2733}
                coordX={-123.1213}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 177 Davie St #100, Vancouver, BC V6Z 2Y1
            </p>
            <ul>
              <li>Operating Hours: 7:00 AM – 10:00 PM daily</li>
              <li>
                Upscale grocery experience with a wide range of local and
                international organic products
              </li>
              <li>
                Gourmet deli, bakery, and loyalty point offers for frequent
                shoppers
              </li>
            </ul>

            {/* SECTION: Organic Certifications */}
            <h2 id="certifications">Understanding Organic Certification</h2>
            <p>
              In Canada, organic products labeled “Certified Organic” must meet
              standards set by the Canadian Food Inspection Agency (CFIA).
              Smaller local farms might use organic practices without formal
              certification; don’t hesitate to ask store staff or farmers about
              their methods if you’re unsure. Look for seals like “Canada
              Organic” or “USDA Organic” to confirm authenticity.
            </p>

            {/* SECTION: Local Regulations & Best Practices */}
            <h2 id="local-regulations">Local Regulations & Best Practices</h2>
            <p>
              <strong>Packaging & Waste:</strong> The City of Vancouver
              encourages minimal plastic use. Bring your own bags and containers
              whenever possible.
            </p>
            <p>
              <strong>Recycling & Composting:</strong> Familiarize yourself with
              Vancouver’s Green Bin Program to compost organic waste correctly.
            </p>
            <p>
              <strong>Producer Transparency:</strong> Farmers’ markets often
              highlight the origins of produce, aligning with local guidelines
              that support transparent labeling.
            </p>

            {/* SECTION: Practical Tips for Organic Shopping */}
            <h2 id="practical-tips">Practical Tips for Organic Shopping</h2>
            <ul>
              <li>
                <strong>Plan Ahead:</strong> Make a list based on recipes to
                avoid overbuying and wasting produce.
              </li>
              <li>
                <strong>Buy In Season:</strong> Seasonal produce is typically
                fresher and more cost-effective.
              </li>
              <li>
                <strong>Compare Prices:</strong> Many stores have weekly flyers
                or online catalogs listing sales.
              </li>
              <li>
                <strong>Bring Reusable Bags:</strong> Reduce waste and
                single-use plastic fees.
              </li>
              <li>
                <strong>Ask Questions:</strong> Store staff and local farmers
                are often happy to share information about farming practices and
                certifications.
              </li>
            </ul>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Is organic produce always more expensive?</h3>
              <p>
                Organic items can be pricier due to stricter farming standards,
                but pricing varies. Keep an eye out for sales and buy in-season
                to save money.
              </p>

              <h3>2. Can I trust all organic labels?</h3>
              <p>
                Look for recognized certification seals like “Canada Organic”
                and “USDA Organic.” Ask in-store if you have doubts.
              </p>

              <h3>3. What if a local farm is not certified?</h3>
              <p>
                Some small farms use organic methods but skip certification due
                to cost. Talk to the seller or store staff about their farming
                practices.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Vancouver’s organic grocery scene is diverse, offering everything
              from zero-waste storefronts to upscale markets. By shopping
              seasonally, comparing prices, and supporting local farmers, you
              can enjoy a healthier lifestyle while nurturing the environment
              and local economy. With the knowledge and locations provided in
              this guide, you’re ready to plan your next shopping trip!
            </p>
            <p>
              <em>
                Disclaimer: Always check official resources or call ahead to
                confirm store hours, availability, and current regulations. If
                you have specific health concerns, consult a nutrition
                professional or medical expert.
              </em>
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}